import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Eye, LogOut, Search, Wifi, WifiOff, RefreshCw, Cpu, Thermometer, ArrowUp, ArrowDown, ArrowUpDown, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESTART_ALERT_THRESHOLD = 10; // restarts/day that trigger the red highlight

const COLUMNS = [
  { key: "worker_name", label: "Worker" },
  { key: "customer_name", label: "Customer" },
  { key: "ip", label: "IP" },
  { key: "model", label: "Model" },
  { key: "hashrate", label: "Hashrate", numeric: true },
  { key: "temperature", label: "Temp", numeric: true },
  { key: "restarts_24h", label: "Restarts", numeric: true },
  { key: "status", label: "Status" },
];

const HelperDashboard = ({ session, onLogout }) => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | online | offline
  const [lastRefresh, setLastRefresh] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  const fetchMachines = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/helper/machines`, {
        headers: { "X-Helper-Token": session?.token || "" },
      });
      setMachines(res.data || []);
      setLastRefresh(new Date());
    } catch (e) {
      // Logged in before helper logins had a token, or the account was disabled.
      if (e?.response?.status === 401) onLogout();
      else console.error(e);
    } finally {
      setLoading(false);
    }
  }, [session, onLogout]);

  useEffect(() => {
    fetchMachines();
    const t = setInterval(fetchMachines, 20000);
    return () => clearInterval(t);
  }, [fetchMachines]);

  const isOnline = (m) => !m.stale && (m.status || "").toLowerCase() === "online";
  const isFlapping = (m) => (m.restarts_24h || 0) > RESTART_ALERT_THRESHOLD;

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Numbers feel more natural starting high-to-low (worst/highest first)
      const col = COLUMNS.find((c) => c.key === key);
      setSortDir(col?.numeric ? "desc" : "asc");
    }
  };

  const filtered = machines.filter((m) => {
    if (statusFilter === "online" && !isOnline(m)) return false;
    if (statusFilter === "offline" && isOnline(m)) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${m.worker_name} ${m.ip} ${m.customer_name} ${m.model} ${m.farm}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = COLUMNS.find((c) => c.key === sortKey);
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av, bv;
      if (sortKey === "status") {
        av = isOnline(a) ? 1 : 0;
        bv = isOnline(b) ? 1 : 0;
      } else {
        av = a[sortKey];
        bv = b[sortKey];
      }
      if (col?.numeric) {
        av = Number(av) || 0;
        bv = Number(bv) || 0;
        return sortDir === "asc" ? av - bv : bv - av;
      }
      av = (av || "").toString().toLowerCase();
      bv = (bv || "").toString().toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sortKey, sortDir]);

  const onlineCount = machines.filter(isOnline).length;
  const offlineCount = machines.length - onlineCount;
  const flappingCount = machines.filter(isFlapping).length;

  const SortHeader = ({ col, align = "left" }) => {
    const active = sortKey === col.key;
    const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <th className={`py-3 px-4 text-${align}`}>
        <button
          onClick={() => handleSort(col.key)}
          className={`inline-flex items-center gap-1 font-medium transition-colors ${
            active ? "text-[#00C2FF]" : "text-gray-400 hover:text-white"
          }`}
          data-testid={`helper-sort-${col.key}`}
        >
          {col.label}
          <Icon size={12} />
        </button>
      </th>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-10">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-[#27272A] px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-[#00C2FF]/15 flex items-center justify-center shrink-0">
              <Eye className="text-[#00C2FF]" size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm truncate">
                {session.display_name || session.username}
              </p>
              <p className="text-gray-500 text-xs">View-only monitoring</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onLogout}
            className="border-[#27272A] text-gray-400 hover:text-white shrink-0"
            data-testid="helper-logout-btn"
          >
            <LogOut size={14} className="mr-1.5" /> Logout
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-5 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="card p-4 border border-[#00E054]/30">
            <div className="flex items-center gap-2 mb-1">
              <Wifi className="text-[#00E054]" size={16} />
              <span className="text-xs text-gray-500">Online</span>
            </div>
            <p className="text-2xl font-bold text-white">{onlineCount}</p>
          </div>
          <div className="card p-4 border border-red-500/30">
            <div className="flex items-center gap-2 mb-1">
              <WifiOff className="text-red-400" size={16} />
              <span className="text-xs text-gray-500">Offline</span>
            </div>
            <p className="text-2xl font-bold text-white">{offlineCount}</p>
          </div>
          <div className="card p-4 border border-red-500/30">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="text-red-400" size={16} />
              <span className="text-xs text-gray-500">Flapping (&gt;{RESTART_ALERT_THRESHOLD}/day)</span>
            </div>
            <p className="text-2xl font-bold text-white">{flappingCount}</p>
          </div>
        </div>

        {/* Search + filter */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search worker, IP, customer..."
              className="pl-9 bg-[#0F0F0F] border-[#27272A] text-white"
              data-testid="helper-search-input"
            />
          </div>
          <div className="flex gap-2">
            <div className="flex bg-[#0A0A0A] border border-[#27272A] rounded-lg p-1">
              {[
                { key: "all", label: "All" },
                { key: "online", label: "Online" },
                { key: "offline", label: "Offline" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setStatusFilter(opt.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    statusFilter === opt.key ? "bg-[#00C2FF] text-black" : "text-gray-400"
                  }`}
                  data-testid={`helper-filter-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={fetchMachines}
              className="border-[#27272A] shrink-0"
              data-testid="helper-refresh-btn"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>

        {lastRefresh && (
          <p className="text-xs text-gray-600 -mt-3">
            Updated {lastRefresh.toLocaleTimeString()} · auto-refreshes every 20s
            {flappingCount > 0 && (
              <span className="text-red-400 font-medium">
                {" "}· {flappingCount} machine{flappingCount === 1 ? "" : "s"} restarting too often
              </span>
            )}
          </p>
        )}

        {/* Machines list */}
        {sorted.length === 0 ? (
          <div className="text-center py-16 text-gray-500 text-sm">
            {loading ? "Loading machines..." : "No machines match this search/filter"}
          </div>
        ) : (
          <>
            <div className="data-table-wrap overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="table-header">
                    {COLUMNS.map((col) => (
                      <SortHeader
                        key={col.key}
                        col={col}
                        align={col.numeric || col.key === "status" ? "right" : "left"}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((m) => {
                    const flapping = isFlapping(m);
                    return (
                      <tr
                        key={`${m.worker_name}-${m.ip}`}
                        className={`table-row ${flapping ? "bg-red-500/10 border-l-2 border-red-500" : ""}`}
                      >
                        <td className="py-3 px-4 text-white font-mono text-sm">{m.worker_name || "—"}</td>
                        <td className="py-3 px-4 text-gray-400">{m.customer_name || "—"}</td>
                        <td className="py-3 px-4 text-gray-300 font-mono text-sm">{m.ip || "—"}</td>
                        <td className="py-3 px-4 text-gray-400">{m.model || "—"}</td>
                        <td className="py-3 px-4 text-right text-gray-300">{m.hashrate ? `${m.hashrate}` : "—"}</td>
                        <td className="py-3 px-4 text-right text-gray-300">{m.temperature ? `${m.temperature}°C` : "—"}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={flapping ? "text-red-400 font-bold" : "text-gray-300"}>
                            {m.restarts_24h || 0}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          {isOnline(m) ? (
                            <span className="text-[#00E054] text-xs font-medium">● Online</span>
                          ) : (
                            <span className="text-red-400 text-xs font-medium">● Offline</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="data-cards">
              {sorted.map((m) => {
                const flapping = isFlapping(m);
                return (
                  <div
                    key={`${m.worker_name}-${m.ip}`}
                    className={`data-card ${flapping ? "bg-red-500/10 border border-red-500" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-white font-semibold font-mono text-sm truncate">{m.worker_name || "—"}</span>
                      {isOnline(m) ? (
                        <span className="text-[#00E054] text-xs font-medium shrink-0">● Online</span>
                      ) : (
                        <span className="text-red-400 text-xs font-medium shrink-0">● Offline</span>
                      )}
                    </div>
                    {flapping && (
                      <div className="flex items-center gap-1.5 mb-2 text-red-400 text-xs font-medium">
                        <AlertTriangle size={12} />
                        Restarted {m.restarts_24h}x today
                      </div>
                    )}
                    <div className="data-card-row">
                      <span className="data-card-label">Customer</span>
                      <span className="text-gray-300 text-sm">{m.customer_name || "—"}</span>
                    </div>
                    <div className="data-card-row">
                      <span className="data-card-label">IP</span>
                      <span className="text-gray-300 text-sm font-mono">{m.ip || "—"}</span>
                    </div>
                    <div className="data-card-row">
                      <span className="data-card-label">Model</span>
                      <span className="text-gray-300 text-sm">{m.model || "—"}</span>
                    </div>
                    <div className="data-card-row">
                      <span className="data-card-label flex items-center gap-1"><Cpu size={11} /> Hashrate</span>
                      <span className="text-gray-300 text-sm">{m.hashrate ? `${m.hashrate}` : "—"}</span>
                    </div>
                    <div className="data-card-row">
                      <span className="data-card-label flex items-center gap-1"><Thermometer size={11} /> Temp</span>
                      <span className="text-gray-300 text-sm">{m.temperature ? `${m.temperature}°C` : "—"}</span>
                    </div>
                    <div className="data-card-row">
                      <span className="data-card-label flex items-center gap-1"><AlertTriangle size={11} /> Restarts (24h)</span>
                      <span className={`text-sm ${flapping ? "text-red-400 font-bold" : "text-gray-300"}`}>
                        {m.restarts_24h || 0}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HelperDashboard;
